import React, { useState, useEffect } from 'react';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import CostForm from '../components/CostForm';
import { confirmDialog } from '../utils/confirm';
import DocViewer from '../components/DocViewer';
import CustomSelect from '../components/CustomSelect';
import { useFormData } from '../hooks/useFormData';
import { companyKey, groupCostsByCompany } from '../utils/company';
import { COST_TYPE_COLORS as TYPE_COLORS } from '../utils/costTypes';

const getFileUrl = (filename) => {
  if (!filename) return null;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');
  return `${base}/uploads/costs/${filename}`;
};

const getAttachments = (c) => (c?.attachments?.length ? c.attachments : (c?.attachment?.filename ? [c.attachment] : []));
const getAttachmentPreview = (c) => {
  const list = getAttachments(c);
  return { urls: list.map((att) => getFileUrl(att.filename)), filenames: list.map((att) => att.originalName || att.filename) };
};

const Costs = () => {
  const { searchQuery, isAdmin } = useAppContext();
  const { companyNames } = useFormData();
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterPlate, setFilterPlate] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await apiEndpoints.costs.list();
      setCosts(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (data, file) => {
    if (editingCost) {
      await apiEndpoints.costs.update(editingCost._id, data, file);
    } else {
      await apiEndpoints.costs.create(data, file);
    }
    await load();
    setShowForm(false);
    setEditingCost(null);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!await confirmDialog('Delete this cost entry?')) return;
    await apiEndpoints.costs.delete(id);
    await load();
  };

  const formatDate = (v) => v ? new Date(v).toLocaleDateString() : '—';

  const plates = [...new Set(costs.map((c) => c.plate).filter(Boolean))];
  const types = [...new Set(costs.map((c) => c.costType).filter(Boolean))];
  // Group case/whitespace-insensitively: "Meridian Tech SRL" and
  // "MERIDIAN TECH SRL" are the same company. `filterCompany` holds the
  // normalised key (`co.key`), never a raw spelling.
  const companyGroups = groupCostsByCompany(costs, companyNames);

  const filtered = costs.filter((c) => {
    if (filterType !== 'all' && c.costType !== filterType) return false;
    if (filterPlate && c.plate !== filterPlate) return false;
    if (filterCompany !== 'all' && companyKey(c.company) !== filterCompany) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match = [c.plate, c.vehicleName, c.company, c.costType, c.description, c.receiptRef].some((v) => v?.toLowerCase().includes(q));
      if (!match) return false;
    }
    return true;
  });

  const total = filtered.reduce((s, c) => s + (c.amount || 0), 0);

  const byType = types.reduce((acc, t) => {
    acc[t] = costs.filter((c) => c.costType === t).reduce((s, c) => s + (c.amount || 0), 0);
    return acc;
  }, {});

  if (loading) return <div className="page-loading">Loading costs...</div>;

  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">Costs</div>
          <div className="ph-sub">Total: {total.toLocaleString(undefined, { minimumFractionDigits: 2 })} RON</div>
        </div>
        {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => { setEditingCost(null); setShowForm(true); }}>+ Add Cost</button>}
      </div>

      {/* By cost type */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
        {Object.entries(byType).map(([type, amt]) => (
          <div key={type} className="stat-card" style={{ borderTop: `3px solid ${TYPE_COLORS[type] || '#9ca3af'}` }}>
            <div className="stat-label">{type.toUpperCase()}</div>
            <div className="stat-value" style={{ fontSize: '18px' }}>{amt.toLocaleString(undefined, { minimumFractionDigits: 0 })}</div>
            <div className="stat-meta">RON</div>
          </div>
        ))}
      </div>

      {/* By company */}
      {companyGroups.length > 0 && (
        <>
          <div style={{ marginTop: '16px', marginBottom: '8px', fontWeight: 600, fontSize: '13px', color: 'var(--text-2)' }}>Spend by Company</div>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            {companyGroups.map((co) => (
              <div key={co.key} className="stat-card" style={{ borderTop: '3px solid #0077b6', cursor: 'pointer' }} onClick={() => setFilterCompany(filterCompany === co.key ? 'all' : co.key)}>
                <div className="stat-label" style={{ fontSize: '10px' }}>{co.name.toUpperCase()}</div>
                <div className="stat-value" style={{ fontSize: '18px' }}>{co.total.toLocaleString(undefined, { minimumFractionDigits: 0 })}</div>
                <div className="stat-meta">RON total</div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="table-filters" style={{ marginTop: '20px' }}>
        <div className="filter-row">
          <CustomSelect name="filterType" className="form-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </CustomSelect>
          <CustomSelect name="filterCompany" className="form-select" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)}>
            <option value="all">All companies</option>
            {companyGroups.map((co) => <option key={co.key} value={co.key}>{co.name}</option>)}
          </CustomSelect>
          <CustomSelect name="filterPlate" className="form-select" value={filterPlate} onChange={(e) => setFilterPlate(e.target.value)}>
            <option value="">All vehicles</option>
            {plates.map((p) => <option key={p} value={p}>{p}</option>)}
          </CustomSelect>
        </div>
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <div className="card-body" style={{ overflowX: 'auto', padding: '0' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>No cost entries found.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '760px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['PLATE / VEHICLE', 'COMPANY', 'DATE', 'TYPE', 'DESCRIPTION', 'RECEIPT REF', 'AMOUNT', 'FILE', ''].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px' }}>
                      <span className="plate">{c.plate}</span>
                      {c.vehicleName && <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '3px' }}>{c.vehicleName}</div>}
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-3)', fontSize: '12px' }}>{c.company || '—'}</td>
                    <td style={{ padding: '12px' }}>{formatDate(c.date)}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: TYPE_COLORS[c.costType] || '#9ca3af', backgroundColor: `${TYPE_COLORS[c.costType] || '#9ca3af'}18` }}>
                        {c.costType}
                      </span>
                    </td>
                    <td style={{ padding: '12px', color: 'var(--text-3)' }}>{c.description || '—'}</td>
                    <td style={{ padding: '12px', color: 'var(--text-3)', fontSize: '12px' }}>{c.receiptRef || '—'}</td>
                    <td style={{ padding: '12px', fontWeight: 700 }}>{c.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })} {c.currency}</td>
                    <td style={{ padding: '12px' }} onClick={(e) => e.stopPropagation()}>
                      {getAttachments(c).length > 0 ? (
                        <button className="btn btn-ghost btn-xs" style={{ color: '#10b981' }} onClick={e => { e.stopPropagation(); setPreviewDoc(getAttachmentPreview(c)); }}>
                          Preview
                        </button>
                      ) : (
                        <span className="btn btn-ghost btn-xs" style={{ color: '#d1d5db', cursor: 'default' }}>Preview</span>
                      )}
                    </td>
                    {isAdmin && (
                      <td style={{ padding: '12px', textAlign: 'right', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                        <button className="btn btn-ghost btn-xs" onClick={() => { setEditingCost(c); setShowForm(true); }}>Edit</button>
                        <button className="btn btn-ghost btn-xs" style={{ color: '#ef4444' }} onClick={(e) => handleDelete(c._id, e)}>Delete</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <CostForm isOpen={showForm} onClose={() => { setShowForm(false); setEditingCost(null); }} cost={editingCost} onSave={handleSave} />
      <DocViewer urls={previewDoc?.urls} filenames={previewDoc?.filenames} onClose={() => setPreviewDoc(null)} />
    </div>
  );
};

export default Costs;
